const FORUM_TOPICS_CACHE_TTL_MS = 2 * 60 * 1000;
const FORUM_TOPICS_CACHE_PURGE_INTERVAL_MS = 60 * 1000;
const MAX_CACHE_ENTRIES = 100;

type CacheEntry = {
  expiresAt: number;
  data: any;
};

export type ForumTopic = {
  id: number;
  title?: string;
  fancy_title?: string;
  slug: string;
  created_at?: string;
  last_posted_at?: string;
  last_poster_username?: string | null;
  posts_count?: number;
  reply_count?: number;
  username?: string | null;
  category_id?: number;
  category_name?: string | null;
  category_slug?: string | null;
  excerpt?: string | null;
  image_url?: string | null;
  latitude?: number | string | null;
  longitude?: number | string | null;
  accuracy?: number | string | null;
  location_latitude?: number | string | null;
  location_longitude?: number | string | null;
  location_accuracy?: number | string | null;
  first_post_cooked?: string | null;
  firstPost?: {
    cooked?: string | null;
  } | null;
  created_by_user?: {
    username?: string | null;
    name?: string | null;
    avatar_url?: string | null;
  } | null;
};

export const getForumTopicReplyCount = (
  topic: Pick<ForumTopic, 'posts_count' | 'reply_count'>,
) => {
  const count = topic.posts_count === null || topic.posts_count === undefined
    ? Number(topic.reply_count ?? 0)
    : Number(topic.posts_count) - 1;

  return Number.isFinite(count) ? Math.max(count, 0) : 0;
};

export type ForumTopicsPage = {
  topics?: ForumTopic[];
  topicCount?: number;
  hasMore?: boolean;
  page?: number;
  categories?: any[];
};

export type ForumTopicFilterSource = {
  parentType: 'Ecoregions' | 'Commons' | 'Communities' | 'Activities';
  spaceType: 'ecoregion' | 'commons' | 'community' | 'activity';
  name: string;
  fallbackName: string;
  parentCategoryName?: string;
  sourcePath: string;
  isCurrentSpace?: boolean;
};

const cache = new Map<string, CacheEntry>();
let purgeIntervalHandle: number | undefined;

const purgeExpiredCacheEntries = () => {
  const now = Date.now();

  cache.forEach((entry, key) => {
    if (entry.expiresAt <= now) {
      cache.delete(key);
    }
  });
};

const enforceCacheSizeLimit = () => {
  while (cache.size > MAX_CACHE_ENTRIES) {
    const oldestKey = cache.keys().next().value;

    if (!oldestKey) {
      return;
    }

    cache.delete(oldestKey);
  }
};

const getForumTopicsCache = (key: string) => {
  const cached = cache.get(key);

  if (!cached) {
    return null;
  }

  if (cached.expiresAt <= Date.now()) {
    cache.delete(key);
    return null;
  }

  cache.delete(key);
  cache.set(key, cached);

  return cached.data;
};

const setForumTopicsCache = (key: string, data: any) => {
  if (cache.has(key)) {
    cache.delete(key);
  }

  cache.set(key, {
    data,
    expiresAt: Date.now() + FORUM_TOPICS_CACHE_TTL_MS,
  });
  enforceCacheSizeLimit();
};

const clearPurgeInterval = () => {
  if (purgeIntervalHandle === undefined) {
    return;
  }

  window.clearInterval(purgeIntervalHandle);
  purgeIntervalHandle = undefined;
};

if (typeof window !== 'undefined') {
  purgeIntervalHandle = window.setInterval(purgeExpiredCacheEntries, FORUM_TOPICS_CACHE_PURGE_INTERVAL_MS);
  window.addEventListener('beforeunload', clearPurgeInterval);

  if (import.meta.hot) {
    import.meta.hot.dispose(() => {
      window.removeEventListener('beforeunload', clearPurgeInterval);
      clearPurgeInterval();
    });
  }
}

const isFirstPageUrl = (url: string) => {
  const query = url.split('?')[1];

  if (!query) {
    return true;
  }

  const page = new URLSearchParams(query).get('page');

  return !page || page === '0';
};

type FetchForumTopicsOptions = {
  bypassCache?: boolean;
  signal?: AbortSignal;
};

export class ForumApiError extends Error {
  status?: number;
  code?: string;

  constructor(message: string, status?: number, code?: string) {
    super(message);
    this.name = 'ForumApiError';
    this.status = status;
    this.code = code;
  }
}

export async function getForumApiError(response: Response, fallbackMessage: string): Promise<ForumApiError> {
  const errorBody = await response.json().catch(() => null);
  const upstreamMessage = typeof errorBody?.error === 'string' ? errorBody.error : '';
  const plainMessage = upstreamMessage && typeof DOMParser !== 'undefined'
    ? new DOMParser().parseFromString(upstreamMessage, 'text/html').body.textContent?.trim()
    : upstreamMessage;
  return new ForumApiError(
    plainMessage || fallbackMessage,
    response.status,
    errorBody?.code
  );
}

export async function ensureForumSession(): Promise<void> {
  const response = await fetch('/api/discourse/session', {
    credentials: 'include',
    cache: 'no-store',
  });

  if (!response.ok) {
    throw await getForumApiError(response, `Forum session check failed: ${response.status}`);
  }
}

export async function fetchForumTopicsJson(url: string, options: FetchForumTopicsOptions = {}) {
  const shouldCache = isFirstPageUrl(url);
  const cached = shouldCache && !options.bypassCache ? getForumTopicsCache(url) : null;

  if (cached) {
    return cached;
  }

  const response = await fetch(url, { signal: options.signal });
  if (!response.ok) {
    throw new Error(`Forum API error: ${response.status}`);
  }

  const data = await response.json();

  if (shouldCache) {
    setForumTopicsCache(url, data);
  }

  return data;
}

export async function fetchLatestForumTopics(limit = 10, options: { clearCache?: boolean } = {}) {
  const cacheParams = new URLSearchParams({ limit: String(limit) });
  const fetchParams = new URLSearchParams(cacheParams);
  if (options.clearCache) {
    fetchParams.set('clearCache', '1');
  }

  const cacheKey = `/api/discourse/latest-topics?${cacheParams.toString()}`;
  const fetchUrl = `/api/discourse/latest-topics?${fetchParams.toString()}`;

  if (options.clearCache) {
    deleteForumTopicCacheEntry(cacheKey);
  }

  return fetchForumTopicsJson(fetchUrl, {
    bypassCache: options.clearCache,
  });
}

export async function fetchFilteredLatestForumTopics(
  sources: ForumTopicFilterSource[],
  page: number,
  limit = 10,
  signal?: AbortSignal
): Promise<ForumTopicsPage> {
  const cacheKey = `/api/discourse/latest-topics/filter:${JSON.stringify({ sources, page, limit })}`;
  const shouldCache = true;
  const cached = shouldCache ? getForumTopicsCache(cacheKey) : null;

  if (cached) {
    return cached;
  }

  const response = await fetch('/api/discourse/latest-topics/filter', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({ sources, page, limit }),
    signal,
  });

  if (!response.ok) {
    throw new Error(`Forum API error: ${response.status}`);
  }

  const data = await response.json();

  if (shouldCache) {
    setForumTopicsCache(cacheKey, data);
  }

  return data;
}

export async function fetchCurrentSpaceForumCategory(
  sources: ForumTopicFilterSource[],
  signal?: AbortSignal
): Promise<{ id: number; name?: string } | null> {
  const data = await fetchFilteredLatestForumTopics(sources, 0, 1, signal);
  const currentCategory = (data.categories || []).find((category: any) => category.isCurrentSpace);

  return currentCategory ? { id: currentCategory.id, name: currentCategory.name } : null;
}

export async function createDiscourseTopic(payload: {
  categoryId?: number;
  spaceTarget?: {
    type: 'commons' | 'ecoregion' | 'community';
    id: string;
  };
  resolveCategoryFromLocation?: boolean;
  title: string;
  description: string;
  imageUrls?: string[];
  latitude?: number;
  longitude?: number;
  accuracy?: number;
}): Promise<{ topicId: number; topicUrl: string }> {
  const response = await fetch('/api/discourse/topics', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/json',
    },
    body: JSON.stringify(payload),
  });

  if (!response.ok) {
    throw await getForumApiError(response, `Forum topic could not be created: ${response.status}`);
  }

  const result = await response.json();
  clearForumTopicsCache();
  return result;
}

export type LocationTargetProgress = {
  stage: 'checking_location' | 'identifying_ecoregion' | 'downloading_ecoregion'
    | 'ecoregion_profile_ready' | 'saving_ecoregion' | 'creating_forum_category'
    | 'ready' | 'failed' | 'publishing_topic' | 'done';
  name?: string;
  categoryId?: number;
  targetType?: 'commons' | 'ecoregion';
  fallback?: boolean;
  code?: string;
  profile?: {
    description: string | null;
    bannerImageUrl: string | null;
    flagshipImageUrl: string | null;
    bioregion: string | null;
  };
};

export async function prepareLocationForumTarget(
  payload: { latitude: number; longitude: number },
  onProgress: (progress: LocationTargetProgress) => void,
): Promise<LocationTargetProgress> {
  const response = await fetch('/api/discourse/location-target/prepare', {
    method: 'POST',
    credentials: 'include',
    headers: {
      'Content-Type': 'application/json',
      Accept: 'application/x-ndjson',
    },
    body: JSON.stringify(payload),
  });
  if (!response.ok) {
    throw await getForumApiError(response, `Forum location could not be prepared: ${response.status}`);
  }

  let pending = '';
  let ready: LocationTargetProgress | null = null;
  let failure: LocationTargetProgress | null = null;
  const consumeLine = (line: string) => {
    if (!line.trim()) {
      return;
    }
    const progress = JSON.parse(line) as LocationTargetProgress;
    onProgress(progress);
    if (progress.stage === 'ready') {
      ready = progress;
    } else if (progress.stage === 'failed') {
      failure = progress;
    }
  };

  if (response.body) {
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    while (true) {
      const { done, value } = await reader.read();
      pending += decoder.decode(value, { stream: !done });
      const lines = pending.split('\n');
      pending = lines.pop() || '';
      lines.forEach(consumeLine);
      if (done) {
        break;
      }
    }
  } else {
    pending = await response.text();
  }
  pending.split('\n').forEach(consumeLine);

  if (failure) {
    throw new ForumApiError(
      'Forum location could not be prepared',
      503,
      failure.code || 'LOCATION_TARGET_PREPARATION_FAILED',
    );
  }

  if (!ready || !Number.isSafeInteger(ready.categoryId) || Number(ready.categoryId) <= 0) {
    throw new Error('Forum location preparation did not return a category');
  }
  return ready;
}

export async function fetchUserLatestForumTopics(
  username: string,
  page: number,
  limit = 10,
  signal?: AbortSignal
): Promise<ForumTopicsPage> {
  const cacheKey = `/api/discourse/latest-topics/user:${JSON.stringify({ username, page, limit })}`;
  const cached = getForumTopicsCache(cacheKey);

  if (cached) {
    return cached;
  }

  const params = new URLSearchParams({
    page: String(page),
    limit: String(limit),
  });
  const response = await fetch(
    `/api/discourse/latest-topics/user/${encodeURIComponent(username)}?${params.toString()}`,
    { signal }
  );

  if (!response.ok) {
    throw new Error(`Forum API error: ${response.status}`);
  }

  const data = await response.json();
  setForumTopicsCache(cacheKey, data);

  return data;
}

export function clearForumTopicsCache() {
  cache.clear();
}

export function deleteForumTopicCacheEntry(key: string) {
  cache.delete(key);
}
